/**
 * Color Schemes for Municipal Portal
 * Define as paletas de cores disponíveis para o portal
 */

export interface ColorScheme {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  background: {
    default: string;
    paper: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
}

/**
 * Neutros únicos do portal (família slate, fria), compartilhados por todas as paletas.
 * As paletas variam só a cor da entidade; texto, bordas e fundos são sempre estes.
 */
export const NEUTRALS = {
  background: {
    default: '#ffffff',
    paper: '#ffffff',
  },
  subtle: '#f8fafc',
  divider: '#e2e8f0',
  text: {
    primary: '#0f172a',
    secondary: '#475569',
  },
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

export const COLOR_SCHEMES: Record<string, ColorScheme> = {
  blue: {
    name: 'Azul Institucional',
    primary: '#1976d2',
    secondary: '#0d47a1',
    accent: '#42a5f5',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  green: {
    name: 'Verde Sustentável',
    primary: '#2e7d32',
    secondary: '#1b5e20',
    accent: '#66bb6a',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  red: {
    name: 'Vermelho Executivo',
    primary: '#c62828',
    secondary: '#b71c1c',
    accent: '#e57373',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  yellow: {
    name: 'Amarelo Energia',
    primary: '#f9a825',
    secondary: '#f57f17',
    accent: '#fdd835',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  gray: {
    name: 'Cinza Corporativo',
    primary: '#546e7a',
    secondary: '#37474f',
    accent: '#78909c',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  purple: {
    name: 'Roxo Moderno',
    primary: '#6a1b9a',
    secondary: '#4a148c',
    accent: '#9c27b0',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  orange: {
    name: 'Laranja Vibrante',
    primary: '#ed6c02',
    secondary: '#e65100',
    accent: '#ff9800',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  brown: {
    name: 'Marrom Clássico',
    primary: '#5d4037',
    secondary: '#3e2723',
    accent: '#8d6e63',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  // Paletas claras: o branco não atinge AA sobre elas, então o tema usa o texto escuro (contrastTextFor)
  sky: {
    name: 'Azul Céu',
    primary: '#4fa3e0',
    secondary: '#2b7bba',
    accent: '#a8d4f5',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  mint: {
    name: 'Verde Menta',
    primary: '#4caf89',
    secondary: '#2e8b66',
    accent: '#a5dcc6',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  lilac: {
    name: 'Lilás',
    primary: '#9575cd',
    secondary: '#6f51a8',
    accent: '#cdbcec',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  pink: {
    name: 'Rosa',
    primary: '#e57399',
    secondary: '#c2507a',
    accent: '#f5b8cc',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
  sand: {
    name: 'Areia',
    primary: '#d4a373',
    secondary: '#a87a4c',
    accent: '#ecd3b8',
    background: NEUTRALS.background,
    text: NEUTRALS.text,
  },
};

export type ColorSchemeType = keyof typeof COLOR_SCHEMES;

export const DEFAULT_COLOR_SCHEME: ColorSchemeType = 'blue';

/**
 * Maps the theme number from parametersPortal to a color scheme
 * @param themeNumber - The theme number from parametersPortal
 * @returns The corresponding color scheme key
 */
export const getColorSchemeByThemeNumber = (themeNumber: number | undefined): ColorSchemeType => {
  if (!themeNumber) return DEFAULT_COLOR_SCHEME;

  switch (themeNumber) {
    case 1:
    case 2:
      return 'yellow';
    case 3:
    case 4:
      return 'blue';
    case 5:
      return 'gray';
    case 6:
      return 'orange';
    case 7:
      return 'brown';
    case 8:
    case 9:
      return 'green';
    case 10:
      return 'red';
    case 11:
      return 'sky';
    case 12:
      return 'mint';
    case 13:
      return 'lilac';
    case 14:
      return 'pink';
    case 15:
      return 'sand';
    case 16:
      return 'purple';
    default:
      return 'blue';
  }
};
